import { Component, For, Show } from "solid-js";
import Button from "./Button";
import { createZodForm } from "../zod-forms";
import { useRPC } from "./RPCProvider";
import SelectFormControl from "./SelectFormControl";
import StringFormControl from "./StringFormControl";
import {
  countryNames,
  foreignCountryEU,
  foreignCountryNonEU,
  isCountry,
} from "~/mydata";
import NumberFormControl from "./NumberFormControl";
import { maybeNumber, maybeType } from "./helpers";
import { Client, editClientOutput, registerClientInput } from "~/trpc/api/clients";
import { z } from "zod";
import FormRow from "./FormRow";

const intoCountry = maybeType(isCountry);
export const EditClientForm: Component<{
  onSuccess: (result: z.output<typeof editClientOutput>) => void;
  clientId: string;
  client: Client
}> = (props) => {
  const rpc = useRPC();
  const client = props.client
  const [Form, form] = createZodForm({
    schema: registerClientInput,
    initialInput: {
      branch: client.branch ?? 0,
      country: client.country,
      vatNumber: client.vatNumber,
      doy: client.doy ?? "",
      activity: client.activity ?? "",
      clientName: client.clientName,
      addressStreet: client.addressStreet ?? "",
      addressNumber: client.addressNumber,
      postalCode: client.postalCode ?? "",
      city: client.city ?? "",
      email: client.email ?? "",
      phone1: client.phone1 ?? "",
      phone2: client.phone2 ?? ""
    },
  });

  const [country, setCountry] = form.bind("country");
  const [activity, setActivity] = form.bind("activity");
  const [clientName, setClientName] = form.bind("clientName");
  const [addressStreet, setAddressStreet] = form.bind("addressStreet");
  const [addressNumber, setAddressNumber] = form.bind("addressNumber");
  const [city, setCity] = form.bind("city");
  const [vatNumber, setVatNumber] = form.bind("vatNumber");
  const [postalCode, setPostalCode] = form.bind("postalCode");
  const [doy, setDoy] = form.bind("doy");
  const [phone1, setPhone1] = form.bind("phone1");
  const [email, setEmail] = form.bind("email");
  const [phone2, setPhone2] = form.bind("phone2");

  return (
    <Form
      class="ms-3 mt-3"
      handleSubmit={async (values) => {
        const reply = await rpc.mutation("updateClient", {
          id: props.clientId,
          ...values
        });
        props.onSuccess(reply);
      }}
    >
      <fieldset class="mb-3">
        <legend>Στοιχεία Πελάτη</legend>
        <FormRow>
          <div class="col me-3">
            <SelectFormControl
              label="Χώρα"
              value={country() ?? ""}
              onChange={(e) => setCountry(intoCountry(e.currentTarget.value))}
              errors={country.dirty && country.issues}
              required
              classList={{
                ["p-2"]: true
              }}
            >
              <option value="GR">GR - Ελλάδα</option>
              <optgroup label="Χώρες Ε.Ε.">
                <For each={foreignCountryEU}>
                  {(code) => (
                    <option value={code}>
                      {code} - {countryNames[code]}
                    </option>
                  )}
                </For>
              </optgroup>
              <optgroup label="Τρίτες Χώρες">
                <For each={foreignCountryNonEU}>
                  {(code) => (
                    <option value={code}>
                      {code} - {countryNames[code]}
                    </option>
                  )}
                </For>
              </optgroup>
            </SelectFormControl>
          </div>
          <div class="col me-3">
            <StringFormControl
              label="Α.Φ.Μ."
              value={vatNumber()}
              onChange={(e) => setVatNumber(e.currentTarget.value)}
              errors={vatNumber.dirty && vatNumber.issues}
              required
              classList={{
                ["p-2"]: true
              }}
            />
          </div>
          <div class="col me-3">
            <Show when={country() === "GR"}>
              <StringFormControl
                label="Δ.Ο.Υ."
                value={doy()}
                onChange={(e) => setDoy(e.currentTarget.value)}
                errors={doy.dirty && doy.issues}
                classList={{
                  ["p-2"]: true
                }}
              />
            </Show>
          </div>
          <div class="col me-3">
            <StringFormControl
              label="Επάγγελμα/Δραστηριότητα"
              value={activity()}
              onChange={(e) => setActivity(e.currentTarget.value)}
              errors={activity.dirty && activity.issues}
              classList={{
                ["p-2"]: true
              }}
            />
          </div>
        </FormRow>
        <FormRow>
          <div class="col me-3">
            <StringFormControl
              label="Επωνυμία"
              value={clientName()}
              onChange={(e) => setClientName(e.currentTarget.value)}
              errors={clientName.dirty && clientName.issues}
              required
              classList={{
                ["p-2"]: true
              }}
            />
          </div>
        </FormRow>
      </fieldset>
      <fieldset class="mb-3">
        <legend>Διεύθυνση</legend>
        <FormRow>
          <div class="col me-3">
            <StringFormControl
              label="Οδός"
              value={addressStreet()}
              onChange={(e) => setAddressStreet(e.currentTarget.value)}
              errors={addressStreet.dirty && addressStreet.issues}
              classList={{
                ["p-2"]: true
              }}
            />
          </div>
          <div class="col-2 me-3">
            <NumberFormControl
              type="number"
              min="0"
              max="9999"
              label="Αριθμός"
              value={addressNumber()}
              onChange={(e) =>
                setAddressNumber(maybeNumber(e.currentTarget.value))
              }
              errors={addressNumber.dirty && addressNumber.issues}
              classList={{
                ["p-2"]: true
              }}
            />
          </div>
          <div class="col-2 me-3">
            <StringFormControl
              label="Ταχυδρομικός Κώδικας"
              value={postalCode()}
              onChange={(e) => setPostalCode(e.currentTarget.value)}
              errors={postalCode.dirty && postalCode.issues}
              classList={{
                ["p-2"]: true
              }}
            />
          </div>
          <div class="col-3 me-3">
            <StringFormControl
              label="Πόλη"
              value={city()}
              onChange={(e) => setCity(e.currentTarget.value)}
              errors={city.dirty && city.issues}
              classList={{
                ["p-2"]: true
              }}
            />
          </div>
        </FormRow>
      </fieldset>
      <fieldset class="mb-3">
        <legend>Στοιχεία επικοινωνίας</legend>
        <FormRow>
          <div class="col me-3">
            <StringFormControl
              label="Διεύθυνση email"
              value={email()}
              onChange={(e) => setEmail(e.currentTarget.value)}
              errors={email.dirty && email.issues}
              classList={{
                ["p-2"]: true
              }}
            />
          </div>
          <div class="col me-3">
            <StringFormControl
              label="Τηλέφωνο-1"
              value={phone1()}
              onChange={(e) => setPhone1(e.currentTarget.value)}
              errors={phone1.dirty && phone1.issues}
              classList={{
                ["p-2"]: true
              }}
            />
          </div>
          <div class="col me-3">
            <StringFormControl
              label="Τηλέφωνο-2"
              value={phone2()}
              onChange={(e) => setPhone2(e.currentTarget.value)}
              errors={phone2.dirty && phone2.issues}
              classList={{
                ["p-2"]: true
              }}
            />
          </div>
        </FormRow>
      </fieldset>
      <FormRow>
        <div class="col-3">
          <Button
            kind="primary"
            type="submit"
            disabled={
              form.submitting() ||
              (form.lastSubmit()?.success === false && !form.valid())
            }
            classList={{
              ["p-2"]: true
            }}
          >
            Αποθήκευση
          </Button>
        </div>
      </FormRow>
    </Form>
  );
};
export default EditClientForm;
